import { v4 as uuidv4 } from 'uuid';
import ProductKpcDictionary from '../../assets/configurator/configurator-kpc-dictionary.json';
import TerminalRulesFile from '../../assets/configurator/configurator-rules.json';

// define the product families that qualify as accessories
const accessoryFamilies = ['accessories', 'cables', 'components'];

/* eslint no-param-reassign: "off", no-console: "off" */
const configuratorStore = {
  namespaced: true,
  state: {
    cart: [],
    activePage: 0,
    terminalWizardPage: 0,
    wholesale: true,
    showPrices: true,
    militaryPricebook: false,
    commercialPricebook: true,
    cartItemUnderEdit: null,
    products: [],
    sessionId: null,
    discountTier: 'wholesalePrice',
    infoBoxVisible: true
  },
  mutations: {
    setSessionId(state) {
      state.sessionId = uuidv4();
    },
    addToCart(state, payload) {
      if (payload.kpc.startsWith('U8')) {
        state.cart.push(payload);
        state.terminalBundle = null;
      } else { // it's an accessory
        const existingCartItemIndex = state.cart.findIndex(s => s.kpc == payload.kpc);
        if (existingCartItemIndex == -1) {
          state.cart.push(payload);
        } else {
          state.cart[existingCartItemIndex].quantity += payload.quantity;
        }
      }
    },
    removeFromCart(state, payload) {
      state.cart = state.cart.filter(i => i.id != payload.id);
    },
    editCartItem(state, payload) {
      // find the index of the existing item
      const existingItemIndex = state.cart.findIndex(item => item.id == payload.id);
      // replace the item at that index with the new item
      state.cart.splice(existingItemIndex, 1, payload);
    },
    emptyCart(state) {
      state.cart = [];
    },
    resetCart(state) {
      state.cart = [];
    },
    setActivePage(state, payload) {
      state.activePage = payload;
    },
    setTerminalWizardPage(state, payload) {
      state.terminalWizardPage = payload;
    },
    setWholesaleFlag(state, payload) {
      state.wholesale = payload;
    },
    setShowPricesFlag(state, payload) {
      state.showPrices = payload;
    },
    setMilitaryPricebookFlag(state, payload) {
      state.militaryPricebook = payload;
    },
    setCommercialPricebookFlag(state, payload) {
      state.commercialPricebook = payload;
    },
    setCartItemUnderEdit(state, payload) {
      if (payload?.originalBuiltTerminal) { // this check is for maintaining cart item quantity back in the edit phase
        payload.originalBuiltTerminal.quantity = payload.quantity;
      }
      state.cartItemUnderEdit = payload;
    },
    fetchProducts(state, payload) {
      state.products = payload;
    },
    setDiscountTier(state, payload) {
      state.discountTier = payload;
    },
    setInfoBoxVisibility(state, payload) {
      state.infoBoxVisible = payload;
    }
  },
  actions: {
    // eslint-disable-next-line no-empty-pattern
    async submitPurchaseOrder({ state }, payload) {
      // append to the payload pricing visibility
      payload.showPricing = state.showPrices;
      payload.showWholesalePricing = state.wholesale;

      try {
        const res = await fetch('/v2/configurator/quote', {
          body: JSON.stringify(payload),
          method: 'POST',
          headers: new Headers({ 'Content-Type': 'application/json' }),
        });
        if (!res.ok) throw new Error(await res.text());
        return true;
      } catch (ex) {
        console.error(ex);
        return false;
      }
    },
    // eslint-disable-next-line no-empty-pattern
    async submitContactForm({ }, payload) {
      try {
        const res = await fetch('/v2/configurator/contact', {
          body: JSON.stringify(payload),
          method: 'POST',
          headers: new Headers({ 'Content-Type': 'application/json' }),
        });
        if (!res.ok) throw new Error(await res.text());
        return true;
      } catch (ex) {
        console.error(ex);
        return false;
      }
    },
    async fetchProducts({ commit }) {
      try {
        const res = await fetch('/v2/info/configuratorProducts');
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        commit('fetchProducts', data);
        return data
      } catch (ex) {
        console.error(ex);
        return null;
      }
    }
  },
  getters: {
    kpcDictionary: () => ProductKpcDictionary,
    rules: () => TerminalRulesFile,
    discountTier: (state) => state.discountTier,
    allProductsIndex: (state) => {
      const index = {};
      state.products.forEach(p => {
        p.wholesalePrice = p[state.discountTier] || p.wholesalePrice;
        index[p.id] = p;
      });
      return index;
    },
    allProducts: (state) => state.products.map(p => ({ ...p, kpc: p.id, wholesalePrice: p[state.discountTier] || p.wholesalePrice })), 
    terminals: (state, getters) => getters.allProducts.filter(p => p.productFamily?.toLowerCase() == 'terminal'),
    connectivity: (state, getters) => getters.allProducts.filter(p => p.productFamily?.toLowerCase() == 'connectivity').sort((a, b) => a.score - b.score),
    accessories: (state, getters) => getters.allProducts.filter(p => accessoryFamilies.includes(p.productFamily?.toLowerCase())),
    warranties: (state, getters) => getters.allProducts.filter(p => p.productFamily?.toLowerCase() == 'warranty'),
    odus: (state, getters) => getters.allProducts.filter(p => p.productFamily?.toLowerCase() == 'odu'),
    configuratorPermissions: (state, getters, rootState, rootGetters) => rootGetters.user.account || {},
    userIsKymeta: (state, getters, rootState, rootGetters) => rootGetters.user?.email.endsWith('@kymetacorp.com'),
    analyticsMetadata: (state, getters, rootState, rootGetters) => ({
      sessionId: state.sessionId,
      accountId: rootGetters.user.accountId,
      accountName: rootGetters.user.accountName,
      availablePricing: getters.configuratorPermissions,
      pricesVisible: state.showPrices
    })
  }
}
export default configuratorStore;
